import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Meta from '../components/Meta';
import './Services.css';
import '../Animations.css';
import './Shared.css';

import fotoWeb from '../assets/foto_web.webp';
import fotoData from '../assets/automation.webp';
import fotoMobile from '../assets/mobile_2.webp';
import fotoSolution from '../assets/datenanylye3.png';
import fotoWebSuccess from '../assets/Grafik.webp';
import fotoOffice from '../assets/office-multi-hero.webp';
import fotoTec1 from '../assets/msvsgoogle.webp';
import fotoTec2 from '../assets/foto1.webp';

const fotos = [fotoWeb, fotoData, fotoMobile, fotoSolution, fotoWebSuccess, fotoOffice, fotoTec1, fotoTec2];

function Services() {
  const { t } = useTranslation();
  const { id } = useParams();
  const serviceCategories = t('services_page.fallback_services', { returnObjects: true });
  const transformationCards = t('services_page.transformation_cards', { returnObjects: true });

  useEffect(() => {
    if (id) {
      const serviceElement = document.getElementById(`service-${parseInt(id, 10) - 1}`);
      if (serviceElement) {
        serviceElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [id]);

  const selectedService = id ? serviceCategories[parseInt(id, 10) - 1] : null;

  return (
    <div className="services-page">
      <Meta
        title={selectedService ? `${selectedService.title} | Vorpoint` : t('services_page.meta_title')}
        description={selectedService ? selectedService.description : t('services_page.meta_description')}
      />

      {/* Hero */}
      <section className="modern-hero">
        <div className="hero-content">
          <motion.div
            className="services-hero-badge"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <span>💡 {t('services_page.digital_transformation_label')}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('services_page.hero_title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('services_page.hero_subtitle')}
          </motion.p>
          <motion.div
            className="services-hero-ctas"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link to="/kontakt" className="services-cta-primary">
              {t('services_page.contact_button')}
            </Link>
            <Link to="/our_cost" className="services-cta-secondary">
              {t('services_page.cost_button')}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Digital Transformation Section */}
      <section className="digital-transformation">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-label">{t('services_page.digital_transformation_label')}</div>
          <h2>{t('services_page.digital_transformation_title')}</h2>
          <p>{t('services_page.digital_transformation_text')}</p>
          <div className="transformation-grid">
            {Array.isArray(transformationCards) && transformationCards.map((card, i) => (
              <motion.div
                key={i}
                className="transformation-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * (i + 1) }}
              >
                <div className="icon-wrapper"><span className="icon">{card.icon}</span></div>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                <Link to="/kontakt" className="learn-more">{t('services_page.contact_button')}</Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* All 8 Service Cards */}
      <div className="full-width-container">
        {serviceCategories.map((category, index) => {
          const isEven = index % 2 === 0;
          const cardClassName = isEven ? 'service-card-services even' : 'service-card-services odd';

          return (
            <motion.div
              key={index}
              id={`service-${index}`}
              className={cardClassName}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, delay: 0.05 }}
            >
              <div className="image-container-services">
                <img src={fotos[index]} alt={category.title} />
                <div className="service-number">0{index + 1}</div>
              </div>
              <div className="text-container-services">
                <h2>{category.title}</h2>
                <h4>{category.description}</h4>
                <p>{category.service_motto}</p>
                <ul>
                  {category.services_list.map((service, sIndex) => (
                    <li key={sIndex}>{service}</li>
                  ))}
                </ul>
                <h3>{category.tech_stack_title}</h3>
                <p className="tech-stack-text">{category.tech_stack}</p>
                <h3>{category.use_cases_title}</h3>
                <ul>
                  {category.use_cases.map((useCase, uIndex) => (
                    <li key={uIndex}>{useCase}</li>
                  ))}
                </ul>
                <h3>{category.ideal_for_title}</h3>
                <p>{category.ideal_for}</p>
                {category.price_range && (
                  <div className="price-badge">
                    <span className="price-label">{category.price_range_title}</span>
                    <span className="price-value">{category.price_range}</span>
                  </div>
                )}
                <div className="service-cta-group">
                  <Link to="/kontakt">
                    <motion.button
                      className="contact-cta-button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t('services_page.contact_button')}
                    </motion.button>
                  </Link>
                  <Link to="/our_cost">
                    <motion.button
                      className="cost-button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t('services_page.cost_button')}
                    </motion.button>
                  </Link>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Final CTA */}
      <section className="final-cta-services">
        <motion.div
          className="final-cta-content-services"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2>{t('services_page.final_cta_title')}</h2>
          <p>{t('services_page.final_cta_text')}</p>
          <div className="final-cta-buttons">
            <Link to="/kontakt">
              <motion.button
                className="cta-button-services primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('services_page.contact_button')}
              </motion.button>
            </Link>
            <Link to="/our_cost">
              <motion.button
                className="cta-button-services secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('services_page.cost_button')}
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

export default Services;